import React, { cloneElement, isValidElement } from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { Tooltip } from './Tooltip.jsx';
import { SwapIcon } from './Swap.jsx';

/**
 * Shell button. Primary is the filled accent action, secondary the pressed
 * toggle fill (hover steps lighter in dark and darker in light), outlined
 * the bordered one, ghost the quiet one.
 * Default is 36px tall, compact 28px.
 * Icons are Lucide components; their stroke thickens on hover. Passing a
 * different icon swaps it in place, so a state (copied, expanded) only needs
 * to change the prop.
 *
 * `render` swaps the root for another element (a link, usually). That path
 * clones the element instead of going through Base UI's `render`, which would
 * add role="button" to a link.
 */
export function Button({
  variant = 'primary',
  size = 'default',
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  className,
  children,
  render,
  href,
  type,
  disabled,
  tooltip,
  ...props
}) {
  const iconSize = size === 'compact' ? 14 : 16;
  // A new icon swaps in place of the old one (Swap.jsx).
  const icon = (Icon) => <SwapIcon icon={Icon} size={iconSize} />;
  const iconOnly = (children == null || children === false || children === '') && Boolean(LeadingIcon || TrailingIcon);
  const shared = {
    className: ['edl-button', className].filter(Boolean).join(' '),
    'data-variant': variant,
    'data-size': size,
    'data-icon-only': iconOnly ? '' : undefined,
    'data-icon-start': !iconOnly && LeadingIcon ? '' : undefined,
    'data-icon-end': !iconOnly && TrailingIcon ? '' : undefined,
    'data-disabled': disabled ? '' : undefined,
  };
  const body = (
    <>
      <span className="edl-button__surface" aria-hidden="true" />
      <span className="edl-button__label">
        {icon(LeadingIcon)}
        {children}
        {icon(TrailingIcon)}
      </span>
    </>
  );
  const withTooltip = (button) => (tooltip && iconOnly ? <Tooltip content={tooltip}>{button}</Tooltip> : button);
  // A native disabled <button> blocks its own click. A link or a custom render
  // has no such guard, so its activation is cancelled here.
  const blocked = disabled ? { onClick: (event) => event.preventDefault() } : null;

  if (isValidElement(render)) {
    return withTooltip(cloneElement(render, {
      ...props,
      ...shared,
      ...blocked,
      className: [shared.className, render.props.className].filter(Boolean).join(' '),
      'aria-disabled': disabled ? true : undefined,
    }, body));
  }

  if (href) {
    return withTooltip(
      <a
        {...props}
        {...shared}
        {...blocked}
        href={disabled ? undefined : href}
        aria-disabled={disabled ? true : undefined}
      >
        {body}
      </a>,
    );
  }

  return withTooltip(
    <ButtonPrimitive {...props} {...shared} disabled={disabled} type={type ?? 'button'}>
      {body}
    </ButtonPrimitive>,
  );
}
